import {
  Directive,
  OnInit,
  Input,
  ViewContainerRef,
  AfterViewInit,
  TemplateRef,
  NgZone,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { FocusMonitor } from '@angular/cdk/a11y';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { debounceTime, filter, fromEvent } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TippyDirective } from '@ngneat/helipopper';

import { FocusManagerService, EditService, RefreshService } from '../services';
import {
  AnyFn,
  anyFnExec,
  CellComponent,
  CellContext,
  CellDisplayConfig,
  CellEditConfig,
  DialogCellConfig,
  DialogData,
  GridColumn,
  IIdentity,
  RowActions,
} from '../models';
import { Constants, DefaultDialogConfig } from '../utils/constants';
import { ColorType } from '@grid/models/color-types.model';
import { ColorService } from '@grid/services/color.service';

@UntilDestroy()
@Directive({
  selector: '[gridCellDef]',
})
export class GridCellDefDirective<T extends IIdentity>
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input('gridCellDef') context!: {
    data: T;
    column: GridColumn<T>;
    rowIndex: number;
    row: {
      actions: RowActions;
      allowDeleting?: AnyFn;
    };
    cell: {
      element: HTMLElement;
      actions: {
        onEdited: (columnsDef?: string[]) => void;
      };
    };
    allowEditing?: boolean;
    allowDeleting?: boolean;
    tooltip: TippyDirective;
  };

  currentState!: 'display' | 'edit';
  shouldEndEditDelegated = false;
  currentDialog?: MatDialogRef<any, any>;

  private showTooltipHandler = this.showTooltip.bind(this);
  private hideTooltipHandler = this.hideTooltip.bind(this);

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private templateRef: TemplateRef<any>,
    private container: ViewContainerRef,
    private focusMonitor: FocusMonitor,
    private focusManager: FocusManagerService,
    private dialog: MatDialog,
    private refreshService: RefreshService,
    private editService: EditService,
    private colorService: ColorService
  ) {}

  private get isManualTooltipTrigger(): boolean {
    return this.context.tooltip.trigger === 'manual';
  }

  //#region Lifecycle hooks

  ngOnInit(): void {
    if (this.context.column.customClassList) {
      this.updateCellClasses();
    }

    if (this.context.column.display) {
      this.enterDisplayMode(true, false);
    } else {
      this.enterEditMode(true, false);
    }

    this.refreshService.refresh$
      .pipe(
        untilDestroyed(this),
        filter((x) => this.context.data.id === x || x === 'ALL_ROWS')
      )
      .subscribe(() => {
        this.refreshCell();
      });

    this.editService.openEdit$
      .pipe(
        untilDestroyed(this),
        filter(
          ({ column, id }) =>
            column === this.context.column.columnDef &&
            id === this.context.data.id
        )
      )
      .subscribe(({ additionalData }) => {
        this.enterEditMode(true, false, additionalData);
      });

    this.editService.closeEdit$
      .pipe(
        untilDestroyed(this),
        filter(() => {
          const editConfig = this.getEditConfig();
          return (
            editConfig?.type &&
            editConfig.mode === 'dialog' &&
            this.currentState === 'edit'
          );
        })
      )
      .subscribe(() => {
        this.enterDisplayMode(true, false);
      });

    this.colorService.colorCell$
      .pipe(
        untilDestroyed(this),
        filter(
          (x) =>
            x.columnDef === this.context.column.columnDef &&
            x.dataId === this.context.data.id &&
            (!!x.color || !!this.context.cell.element.style.backgroundColor)
        )
      )
      .subscribe((x) => {
        this.colorCell(x.color);
      });
  }

  ngAfterViewInit(): void {
    this.focusMonitor
      .monitor(this.context.cell.element, true)
      // add debounce to improve keyboard navigation when continuously pressing tab
      .pipe(debounceTime(50))
      .subscribe((origin) => {
        // TODO(Grid) merge this focus manager with angular focus monitor
        this.focusManager.focus(origin ? this.context.cell.element : null);

        origin ? this.enterEditMode() : this.enterDisplayMode();
      });

    if (this.context.column.bindClickEvent) {
      // main usage of this listener is to give user the possibility to reenter edit state for a cell that is already focused
      // cells that are already focused will not receive the refocus event in order to enter edit mode
      // e.q dialog editing, reopen dialog after being closed
      fromEvent(
        this.templateRef.elementRef.nativeElement.parentElement,
        'click'
      )
        .pipe(
          untilDestroyed(this),
          filter(() => this.currentState === 'display')
        )
        .subscribe(() =>
          this.focusMonitor.focusVia(this.context.cell.element, 'mouse')
        );
    }
  }

  ngOnDestroy(): void {
    this.focusMonitor.stopMonitoring(this.context?.cell?.element);
    // close dialog on grid cell destroy
    this.currentDialog?.close();
  }

  //#endregion

  private updateCellClasses(): void {
    const cellElement = (
      this.templateRef.elementRef.nativeElement as HTMLElement
    ).parentElement;

    cellElement.classList.add(...this.context.column.customClassList);
  }

  private setDelegateEditState(value: boolean): void {
    this.shouldEndEditDelegated = value;
  }

  private enterDisplayMode(delegated = false, rerender = true): void {
    // check if already in display mode or there is a display state defined
    if (
      (this.currentState === 'display' && !delegated) ||
      !this.context.column?.display
    ) {
      return;
    }

    // prevent entering display mode unless the end edit is not delegated or the call is made from a delegated party
    if (this.shouldEndEditDelegated && !delegated) {
      console.log('unable to enter display mode: delegated edit');
      return;
    }

    this.shouldEndEditDelegated = false;

    const displayConfig = this.getDisplayConfig();

    if (!displayConfig?.type) {
      return;
    }

    if (this.isManualTooltipTrigger) {
      this.hideTooltip();
      this.addCellEventListeners();
    }

    this.currentState = 'display';

    this.updateView(displayConfig, rerender);

    // if dialog is open, close it when entering display mode
    this.currentDialog?.close();
    this.currentDialog = null;

    if (rerender) {
      // we need to do this hack in order to retrigger resizeObserver from tooltip library
      // scenario: short text changed to overflow text after editing it
      this.repaintCell();
    }
  }

  private enterEditMode(
    delegated = false,
    rerender = true,
    additionalData?: any
  ): void {
    // check if already in edit mode or there is no edit state defined
    if (this.currentState === 'edit' || !this.context.column?.edit) {
      return;
    }

    const editConfig = this.getEditConfig();

    if (!editConfig?.type) {
      return;
    }

    // check if start edit is externally delegated and the call is a delegated one
    if (editConfig?.delegateStartEdit && !delegated) {
      console.log('unable to enter edit mode: delegated edit');
      return;
    }

    if (this.isManualTooltipTrigger) {
      this.removeCellEventListeners();
      this.showTooltip();
    }

    const allowEditing = this.isEditingAllowed(editConfig);

    if (editConfig.mode === 'dialog') {
      this.currentState = 'edit';
      this.shouldEndEditDelegated = true;

      this.startDialogEditing(editConfig, additionalData, allowEditing);
    } else {
      if (!allowEditing) {
        return;
      }

      this.currentState = 'edit';

      this.shouldEndEditDelegated = editConfig.delegateEndEdit ?? false;

      this.updateView(editConfig, rerender);
    }
  }

  private getDisplayConfig(): CellDisplayConfig<T> | undefined {
    return anyFnExec(
      this.context.column.display,
      this.context.data,
      this.context.cell.element
    );
  }

  private getEditConfig(): CellEditConfig<T> | undefined {
    return anyFnExec(
      this.context.column.edit,
      this.context.data,
      this.context.cell.element
    );
  }

  private startDialogEditing(
    gridEdit: CellEditConfig<T>,
    additionalData?: any,
    allowEditing?: boolean
  ): void {
    const editConfig = gridEdit?.config as DialogCellConfig<T>;

    const dialogAdditionalData = {
      ...(additionalData ?? {}),
      ...(editConfig?.additionalData ?? {}),
    };

    const data: DialogData<T> = {
      context: {
        column: this.context.column,
        data: this.context.data,
        allowEditing,
        additionalData: dialogAdditionalData,
      },
    };

    editConfig.beforeOpenAdaptor?.(this.context.data, dialogAdditionalData);

    let prevPanelClasses =
      (gridEdit.config as MatDialogConfig<T>).panelClass ?? [];
    prevPanelClasses = Array.isArray(prevPanelClasses)
      ? (prevPanelClasses as string[])
      : [prevPanelClasses as string];

    this.currentDialog = this.dialog.open(gridEdit.type, {
      ...DefaultDialogConfig,
      ...(gridEdit.config as MatDialogConfig<T>),
      panelClass: [
        ...prevPanelClasses,
        `qa-dialog-${this.context.column.columnDef}`,
      ],
      data,
    });

    this.currentDialog
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result: Partial<T>) => {
        if (result) {
          const columnsDef = Object.keys(result);
          columnsDef.forEach((x: string) => {
            const columnDef = x as keyof T;
            this.context.data[columnDef] = result[columnDef] as any;
          });

          this.context.cell.actions.onEdited(columnsDef);
        }

        editConfig.afterCloseAdaptor?.(result, dialogAdditionalData);

        // restore focus to cell
        this.focusMonitor.focusVia(this.context.cell.element, 'mouse');

        // reenter display mode so that we can reopen the dialog
        // set timeout of 50 millisec because focus has a debounce
        setTimeout(() => {
          this.enterDisplayMode(true);
        }, 50);
      });
  }

  private updateView(
    cellConfig: CellDisplayConfig<T> | CellEditConfig<T>,
    rerender: boolean
  ): void {
    this.container.clear();

    // TODO(Grid) not this functions responsibility, move outside
    const cellContext: CellContext<T> = {
      column: this.context.column,
      data: this.context.data,
      cell: {
        element: this.context.cell.element,
        actions: {
          endEdit: this.enterDisplayMode.bind(this, true),
          startEdit: this.enterEditMode.bind(this, true, true),
          focus: this.focusMonitor.focusVia.bind(
            this.focusMonitor,
            this.context.cell as any,
            'program'
          ),
          setDelegateEditState: this.setDelegateEditState.bind(this),
          onEdited: this.context.cell.actions.onEdited,
        },
        allowEditing: this.isEditingAllowed(cellConfig),
      },
      config: cellConfig.config,
      parameters: cellConfig.parameters,
      row: {
        index: this.context.rowIndex,
        allowDeleting: this.isDeletingAllowed(),
        actions: this.context.row.actions,
      },
    };

    if (cellConfig.type instanceof TemplateRef) {
      this.container.createEmbeddedView(cellConfig.type, {
        $implicit: cellContext,
      });
    } else {
      const component = this.container.createComponent(cellConfig.type);

      (component.instance as CellComponent<T>).context = cellContext;
    }

    if (rerender) {
      // Workaround for the fact that focus monitor(cdkFocusChange) emits outside NgZone.
      this.ngZone.run(() => this.cdr.markForCheck());
    }
  }

  private isEditingAllowed(
    config: CellDisplayConfig<T> | CellEditConfig<T>
  ): boolean {
    return (
      // grid level
      (this.context.allowEditing ?? true) &&
      // column level
      (this.context.column.allowEditing ?? true) &&
      // cell level
      anyFnExec(config.allowEditing ?? true, this.context.data)
    );
  }

  private isDeletingAllowed(): boolean {
    return (
      // grid level
      (this.context.allowDeleting ?? true) &&
      // row level
      anyFnExec(this.context.row.allowDeleting ?? true, this.context.data)
    );
  }

  private refreshCell(): void {
    if (this.currentState === 'edit') {
      return;
    }

    this.enterDisplayMode(true, true);
  }

  private repaintCell(): void {
    this.context.cell.element.classList.add('repaint');
    setTimeout(() => {
      this.context.cell.element.classList.remove('repaint');
    }, 50);
  }

  private colorCell(color: ColorType): void {
    this.context.cell.element.style.backgroundColor =
      !color?.hexCode ||
      color.hexCode === Constants.DefaultValues.colorReset.hexCode
        ? ''
        : color.hexCode;
  }

  private showTooltip(): void {
    this.context.tooltip.show();
  }

  private hideTooltip(): void {
    this.context.tooltip.hide();
  }

  private addCellEventListeners(): void {
    this.context.cell.element.addEventListener(
      'mouseenter',
      this.showTooltipHandler
    );
    this.context.cell.element.addEventListener(
      'mouseleave',
      this.hideTooltipHandler
    );
  }

  private removeCellEventListeners(): void {
    this.context.cell.element.removeEventListener(
      'mouseenter',
      this.showTooltipHandler
    );
    this.context.cell.element.removeEventListener(
      'mouseleave',
      this.hideTooltipHandler
    );
  }
}
